import React, { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import type { IncidentGroup } from '../../services/incidentsApi';
import IncidentTrendCard from './IncidentTrendCard';
import HotspotsCard from './HotspotsCard';

export interface InsightBandProps {
  groups: IncidentGroup[];
  zoneNameById: Map<string, string>;
  cameraNameById: Map<string, string>;
  onSelectHotspot: (target: { type: 'zone' | 'camera'; id: string }) => void;
  hotspotRef?: React.RefObject<HTMLDivElement | null>;
  /** Investigate grouped below-table drill-down. Hidden until a pattern is selected. */
  presentation?: 'card' | 'groupedDrilldown';
  selectedGroup?: IncidentGroup | null;
  hasRows?: boolean;
  tableMode?: 'grouped' | 'raw';
}

const InsightBand: React.FC<InsightBandProps> = ({
  groups,
  zoneNameById,
  cameraNameById,
  onSelectHotspot,
  hotspotRef,
  presentation = 'card',
  selectedGroup = null,
  hasRows = true,
  tableMode,
}) => {
  const [trendExpanded, setTrendExpanded] = useState(false);
  const groupedDrilldown = presentation === 'groupedDrilldown';

  if (tableMode === 'raw') {
    return null;
  }

  if (groupedDrilldown && (!selectedGroup || !hasRows)) {
    return null;
  }

  const trendAccordion = (
    <Accordion
      expanded={trendExpanded}
      onChange={(_, expanded) => setTrendExpanded(expanded)}
      disableGutters
      elevation={0}
      data-testid="incident-risk-trend-panel"
      sx={{
        border: groupedDrilldown ? 'none' : '1px solid',
        borderTop: groupedDrilldown ? '1px solid' : undefined,
        borderColor: groupedDrilldown ? 'divider' : 'divider',
        borderRadius: groupedDrilldown ? 0 : 2.5,
        bgcolor: 'transparent',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: groupedDrilldown ? 0 : 1.5 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {groupedDrilldown ? 'Trend' : 'Pattern Trend'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {groupedDrilldown
              ? 'Last 7 days'
              : 'Last 7 days — open when you need recurrence context beyond the review queue'}
          </Typography>
        </Box>
      </AccordionSummary>
      {trendExpanded ? (
        <AccordionDetails sx={{ pt: 0, px: groupedDrilldown ? 0 : 1.5, pb: 1.5 }}>
          <IncidentTrendCard embedded={groupedDrilldown} />
        </AccordionDetails>
      ) : null}
    </Accordion>
  );

  if (groupedDrilldown) {
    return (
      <Box
        data-testid="recurrence-drilldown-band"
        sx={{
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box ref={hotspotRef}>
          <HotspotsCard
            presentation="groupedDrilldown"
            tableMode={tableMode}
            selectedGroup={selectedGroup}
            groups={groups}
            zoneNameById={zoneNameById}
            cameraNameById={cameraNameById}
            onSelectHotspot={onSelectHotspot}
          />
        </Box>
        {trendAccordion}
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box ref={hotspotRef}>
          <HotspotsCard
            tableMode={tableMode}
            groups={groups}
            zoneNameById={zoneNameById}
            cameraNameById={cameraNameById}
            onSelectHotspot={onSelectHotspot}
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        {trendAccordion}
      </Grid>
    </Grid>
  );
};

export default InsightBand;
